import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMenuPosition } from '../../../FlowchartView/TaskNode/StatusDropdown/hooks/useMenuPosition';
import { chooseOnPress, commitAfterPaint, hideMenuNow } from '../../StatusDropdown/hooks/paintChoice';

/**
 * CustomFieldDropdown Component
 * 
 * Dropdown for custom select fields.
 * Styled to match PriorityDropdown/DifficultyDropdown.
 * Uses portal to render menu outside the transformed canvas.
 */
const CustomFieldDropdown = ({
  field,
  value,
  onChange,
  taskId,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Menu positioning hook (reuse from StatusDropdown)
  const { menuPosition, openMenu } = useMenuPosition(btnRef, menuRef, showMenu);

  // Get current selected option
  const selectedOption = field.options?.find(opt => opt.id === value);
  const displayLabel = selectedOption?.label || 'None';
  const displayColor = selectedOption?.color || '#6b7280';

  // Close menu when clicking outside
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setShowMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [showMenu]);

  // Close menu when canvas starts panning (trackpad or mouse drag)
  useEffect(() => {
    if (!showMenu) return;

    const handlePanStart = () => setShowMenu(false);
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [showMenu]);

  const handleClick = (e) => {
    e.stopPropagation();
    
    if (!showMenu) {
      openMenu(e);
    }
    
    setShowMenu(!showMenu);
  };

  const handleSelect = useCallback((optionId) => {
    const option = field.options?.find((item) => item.id === optionId);
    const label = option?.label || 'None';
    const color = option?.color || '#6b7280';
    hideMenuNow(menuRef.current);
    const button = btnRef.current;
    if (button) {
      button.style.setProperty('--field-color', color);
      button.style.setProperty('--field-bg', `${color}15`);
      const text = button.querySelector('.custom-field-dropdown__label');
      const dot = button.querySelector('.custom-field-dropdown__dot');
      if (text) text.textContent = label;
      if (dot) dot.style.background = color;
    }
    commitAfterPaint(() => {
      onChange?.(taskId, field.id, optionId);
      setShowMenu(false);
    });
  }, [onChange, taskId, field.id, field.options]);

  // Menu content with portal
  const menuContent = showMenu && (
    <div 
      ref={menuRef}
      className="custom-field-dropdown__menu"
      style={{ 
        position: 'fixed',
        top: menuPosition.top, 
        left: menuPosition.left,
        zIndex: 10000,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {/* None option */}
      <button
        className={`custom-field-dropdown__option ${!value ? 'custom-field-dropdown__option--active' : ''}`}
        onMouseDown={(event) => chooseOnPress(event, () => handleSelect(null))}
      >
        <span 
          className="custom-field-dropdown__option-dot" 
          style={{ background: '#6b7280' }}
        />
        <span className="custom-field-dropdown__option-label">None</span>
      </button>
      {/* Field options */}
      {field.options?.map(option => (
        <button
          key={option.id}
          className={`custom-field-dropdown__option ${option.id === value ? 'custom-field-dropdown__option--active' : ''}`}
          onMouseDown={(event) => chooseOnPress(event, () => handleSelect(option.id))}
        >
          <span 
            className="custom-field-dropdown__option-dot" 
            style={{ background: option.color }}
          />
          <span className="custom-field-dropdown__option-label">{option.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <div className="custom-field-dropdown__row">
        <span className="custom-field-dropdown__field-label">{field.name}</span>
        <button 
          ref={btnRef}
          className="custom-field-dropdown__btn"
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            '--field-color': displayColor,
            '--field-bg': `${displayColor}15`,
          }}
        >
          <span 
            className="custom-field-dropdown__dot" 
            style={{ background: displayColor }}
          />
          <span className="custom-field-dropdown__label">
            {displayLabel}
          </span>
          <span className="custom-field-dropdown__arrow">▾</span>
        </button>
      </div>

      {showMenu && createPortal(menuContent, document.body)}
    </>
  );
};

export default CustomFieldDropdown;
